import React, { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import { DynamicImage, PrimaryButton, Text } from 'components';
import { mobile, tablet, useQuery } from 'styles/breakpoints';
import theme, { Typography } from 'utils/theme';
import { Question, QuizAnswer } from 'types/quiz';
import Header from './Header';
import { useSelector } from 'react-redux';
import { AppState } from 'state/types';
import useQuizAnswersBirthDetails from 'utils/hooks/useQuizAnswersBirthDetails';
import { Horoscope, Origin } from 'circular-natal-horoscope-js';
import quizSvgs from 'utils/constants/quizSvgs';
import CircleType from 'circletype';
import BirthChart from './BirthChart';
import { resolveBookCoverName } from 'utils/hooks/resolveBookCoverName';

interface BookReviewProps {
  onContinue: (value: object) => void;
  handleBack: () => void;
}

const BookReview: FC<BookReviewProps> = ({ handleBack, onContinue }) => {
  const quiz_answers = useSelector(
    (state: AppState) => state.user.quiz_answers,
  );
  const color = quiz_answers?.color;
  const contentColor = quiz_answers?.contentColor;

  const birthDetails = useQuizAnswersBirthDetails();

  const [showName, setShowName] = useState<boolean>(false);
  const { isTablet } = useQuery();

  const origin = new Origin({
    year: birthDetails?.year,
    month: birthDetails?.month - 1, // 0 = January, 11 = December! That's why -1.
    date: birthDetails?.day,
    hour: birthDetails?.hours,
    minute: birthDetails?.minutes,
    latitude: quiz_answers?.birth_place.lat,
    longitude: quiz_answers?.birth_place.lng,
  });

  const horoscope = new Horoscope({
    origin,
  });
  const ascendantValue = horoscope.Ascendant.Sign.key;
  const celestialBodies = horoscope.CelestialBodies;

  const extractPlanetSigns = data => {
    const obj = {};

    for (let planet of data.all) {
      obj[planet.key] = planet.Sign.key;
    }
    obj['ascendant'] = ascendantValue;
    return obj;
  };

  const planets = extractPlanetSigns(celestialBodies);

  const renderSvgImage = (svgKey: string) => {
    const Svg = quizSvgs[svgKey];
    return <Svg />;
  };

  const textRefPlace = useRef<HTMLDivElement>(null);
  const textRefDate = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRefPlace.current) {
      new CircleType(textRefPlace.current).dir(-1).radius(150);
    }
    if (textRefDate.current) {
      new CircleType(textRefDate.current).dir(-1).radius(120);
    }
  });

  useEffect(() => {
    setTimeout(
      () =>
        window.scrollTo({
          top: 1,
        }),
      100,
    );
  }, []);

  const handleOnLoad = () => {
    setShowName(true);
  };

  const { isNameLongerThan10, isSurameLongerThan10, isSurname, formattedName } =
    resolveBookCoverName(quiz_answers?.name, quiz_answers?.surname);

  return (
    <>
      <Container>
        <ImageWrapper>
          <ImageContainer>
            <StyledDynamicImage
              src={`booksCovers/${color}.png`}
              alt=""
              // handleOnLoad={handleOnLoad}
            />
            <BirthChartStyled
              year={birthDetails?.year as number}
              month={birthDetails?.month as number}
              day={birthDetails?.day as number}
              hour={birthDetails?.hours as number}
              minute={birthDetails?.minutes as number}
              latitude={quiz_answers?.birth_place.lat}
              longitude={quiz_answers?.birth_place.lng}
              color={contentColor}
              width={isTablet ? 195 : 236}
              height={isTablet ? 195 : 236}
            />
            <Name
              color={color}
              dangerouslySetInnerHTML={{ __html: formattedName }}
              isSurname={isSurname}
              isNameLongerThan10={isNameLongerThan10}
              isSurameLongerThan10={isSurameLongerThan10}
            />
            <Date color={color} ref={textRefDate}>
              {birthDetails && birthDetails.fullDate}
            </Date>

            <City color={color} ref={textRefPlace}>
              {`${quiz_answers?.birth_place.main_text}, ${quiz_answers?.birth_place.secondary_text}`}
            </City>
            <SunSign color={contentColor}>
              <SunText color={color}>SUN SIGN</SunText>
              <SvgSign>
                {renderSvgImage(`${color}${quiz_answers?.zodiacSign}`)}
              </SvgSign>
            </SunSign>
            <MoonSign color={contentColor}>
              <SunText color={color}>MOON SIGN</SunText>
              <SvgSign>{renderSvgImage(`${color}${planets?.moon}`)}</SvgSign>
            </MoonSign>
          </ImageContainer>
        </ImageWrapper>
        <ButtonsWrapper>
          <StyledButton onClick={() => onContinue(planets)}>
            Love it!
          </StyledButton>
          <StyledInvertedButton onClick={handleBack} fontColor="#000">
            Change it
          </StyledInvertedButton>
        </ButtonsWrapper>
      </Container>
    </>
  );
};

export default BookReview;

const ImageWrapper = styled.div`
  position: relative;
  max-width: 28.75rem;
  width: 100% !important;
`;

const StyledButton = styled(PrimaryButton)`
  max-width: 28.75rem;
  width: 100%;
  margin: 0 auto;
`;

const StyledInvertedButton = styled(StyledButton)`
  background: #f3f3f8 !important;
  border: 1px solid #000;
  div {
    color: #000;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  div#paper {
    position: absolute !important;
    top: 117px;
    right: 50%;
    transform: translateX(50%);
  }
  @media ${tablet} {
    div#paper {
      top: 96px;
    }
  }
`;

const StyledDynamicImage = styled(DynamicImage)`
  width: 343px;
  background-size: contain;
  @media ${tablet} {
    width: 280px;
  }
`;

const BirthChartStyled = styled(BirthChart)`
  position: absolute !important;
  top: 0;
  div#paper {
    position: absolute !important;
    top: 0;
  }
`;

const SunSign = styled.div<{ color: string }>`
  display: flex;
  width: 46.773px;
  height: 46.773px;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-radius: 23.386px;
  border: 0.532px solid ${({ color }) => color};
  position: absolute;
  bottom: 19px;
  left: 22px;
  gap: 2px;
  @media ${tablet} {
    width: 38px;
    height: 38px;
    bottom: 15px;
    left: 18px;
    gap: 2px;
  }
`;

const MoonSign = styled(SunSign)`
  bottom: 19px;
  right: 20px;
  left: unset;
  @media ${tablet} {
    bottom: 15px;
    right: 19px;
  }
`;
const SunText = styled.p<{ color: string }>`
  color: ${({ color }) => (color === 'beige' ? '#734F3E' : '#fff')};
  text-align: center;
  font-size: 6.236px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  font-family: 'Montserrat', sans-serif;
  max-width: 20px;
  position: relative;
  text-transform: uppercase;
  @media ${tablet} {
    font-size: 5px;
    max-width: 18px;
  }
`;

const SvgSign = styled.div`
  width: 15.591px;
  height: 15.591px;
  position: relative;
  svg {
    position: absolute;
  }
  @media ${tablet} {
    width: 13px;
    height: 13px;
  }
`;

const Name = styled.p<{
  color: string;
  isSurname: boolean;
  isNameLongerThan10: boolean;
  isSurameLongerThan10: boolean;
}>`
  color: ${({ color }) => (color === 'beige' ? '#734F3E' : '#fff')};
  text-align: center;
  font-family: 'Arno Pro';
  font-size: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
    isNameLongerThan10 || isSurameLongerThan10 ? '19px' : '21.827px'};
  font-style: normal;
  font-weight: 400;
  line-height: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
    isNameLongerThan10 || isSurameLongerThan10 ? '20px' : '22px'};
  letter-spacing: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
    isNameLongerThan10 || isSurameLongerThan10 ? '2px' : '3px'};
  text-transform: uppercase;
  position: absolute;
  ${({ isSurname }) => !isSurname && 'top: 5.3rem;'}
  ${({ isSurname, isNameLongerThan10, isSurameLongerThan10 }) =>
    isSurname && (isNameLongerThan10 || isSurameLongerThan10) && 'top: 4.8rem;'}
  ${({ isSurname, isNameLongerThan10, isSurameLongerThan10 }) =>
    isSurname &&
    !(isNameLongerThan10 || isSurameLongerThan10) &&
    'top: 4.6rem;'}
  right: 50%;
  transform: translateX(50%);
  text-wrap: nowrap;
  @media ${tablet} {
    font-size: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
      isNameLongerThan10 || isSurameLongerThan10 ? '14px' : '18px'};
    line-height: ${({ isNameLongerThan10, isSurameLongerThan10 }) =>
      isNameLongerThan10 || isSurameLongerThan10 ? '15px' : '16px'};
    top: ${({ isSurname }) => (isSurname ? '4rem' : '4.5rem')};
  }
`;

const Date = styled.p<{ color: string }>`
  color: ${({ color }) => (color === 'beige' ? '#734F3E' : '#fff')};
  font-family: 'Montserrat';
  font-size: 9.288px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.3rem; /* Reduced letter-spacing */
  position: absolute;
  bottom: 4.1rem;
  right: 50%;
  transform: translateX(50%);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 200px; /* Adjust the width as needed */
  text-transform: capitalize;
  @media ${tablet} {
    font-size: 7px;
    letter-spacing: 0.3rem;
    width: 100px;
    bottom: 3.4rem;
  }
`;

const City = styled(Date)<{ color: string }>`
  color: ${({ color }) => (color === 'beige' ? '#734F3E' : '#fff')};
  bottom: 3rem;
  @media ${tablet} {
    bottom: 2.6rem;
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  padding: 3rem 0 11rem;
  gap: 1.5rem;
  @media ${mobile} {
    padding: 1.5rem 0;
    gap: 0.75rem;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem 1rem;
  width: 100%;
  @media ${tablet} {
    padding: 1rem;
  }
`;
